/** @jsxImportSource @emotion/react */
import { Box, useTheme } from '@mui/material'
import { formBox, formBoxRow, formHeader, valuesFormContainer } from './ValuesForm.styles'
import { usePrioritisationContext } from '@app/contexts/ModuleContexts/PrioritisationContext'
import { useState, useEffect } from 'react'
import { Button } from '@app/components/Atoms/Button'
import { PrioritySlider } from '../../PrioritySlider'
import { convertIntegerToWords } from '@app/utils/numbers.utils'
import pluralize from 'pluralize'
import { capitalizeFirstLetter } from '@app/utils/strings.utils'
import { Carousel } from '@app/components/Atoms/Carousel'

export const ValuesForm = ({
  updatedValueWeightings,
}: {
  updatedValueWeightings: (valueWeightings: { [key: string]: number }) => void
}) => {
  const theme = useTheme()
  const [showElements, setShowElements] = useState<{ [key: string]: boolean }>({})
  const { valueWeightings, elementGroups, assessment } = usePrioritisationContext()
  const [sliderValues, setSliderValues] = useState<{ [key: string]: number }>({})
  const [activeIndex, setActiveIndex] = useState<number>(0)

  useEffect(() => {
    const initialSliderValues = elementGroups.reduce(
      (acc, { value_set_id }) => {
        acc[value_set_id] = valueWeightings[value_set_id] ?? 1
        return acc
      },
      {} as { [key: string]: number },
    )
    setSliderValues(initialSliderValues)
  }, [elementGroups, valueWeightings])

  useEffect(() => {
    updatedValueWeightings(sliderValues)
  }, [sliderValues, updatedValueWeightings])

  const resetShowElements = () => {
    setShowElements(
      elementGroups.reduce(
        (acc, { value_set_id }) => {
          acc[value_set_id] = false
          return acc
        },
        {} as { [key: string]: boolean },
      ),
    )
  }

  return (
    <Box css={valuesFormContainer()}>
      <Box css={formHeader}>
        <h4>
          YOUR ORGANISATION HAS {convertIntegerToWords(elementGroups.length).toUpperCase()} VALUE{' '}
          {pluralize('SET', elementGroups.length)}
        </h4>
        <Box className="buttons">
          <Button
            style={{ color: `${theme.palette.secondary.main}` }}
            variant="text"
            onClick={() => {
              setSliderValues({
                ...sliderValues,
                ...valueWeightings,
                ...assessment?.value_set_config.value_set_weights,
              })
              resetShowElements()
            }}
          >
            Use defaults
          </Button>
          <Button
            variant="text"
            style={{ color: `${theme.palette.secondary.main}` }}
            onClick={() => {
              setSliderValues((prev) => {
                return Object.keys(prev).reduce(
                  (acc, key) => {
                    acc[key] = 3
                    return acc
                  },
                  {} as { [key: string]: number },
                )
              })
              resetShowElements()
            }}
          >
            Balance
          </Button>
        </Box>
      </Box>
      <Box css={formBoxRow}>
        <Carousel
          childWidth={420}
          childGap={32}
          repeating
          childWidthIsPercentage={false}
          onFocusChange={(index) => {
            resetShowElements()
            setActiveIndex(index)
          }}
        >
          {elementGroups.map(({ value_set_id, elements }, index) => (
            <Box
              key={value_set_id}
              css={formBox({
                theme,
                showElements: showElements[value_set_id],
                isActive: activeIndex === index,
              })}
            >
              <Box className="content">
                <Box className="elementList">
                  <Box className="header">
                    <h2>Elements in {value_set_id} set</h2>
                  </Box>
                  <Box className="elementBullets">
                    <Box>
                      {elements
                        .filter((_, index) => index % 2 === 0)
                        .map((element) => (
                          <ul key={element.type}>
                            <li>{element.label}</li>
                          </ul>
                        ))}
                    </Box>
                    <Box>
                      {elements
                        .filter((_, index) => index % 2 === 1)
                        .map((element) => (
                          <ul key={element.type}>
                            <li>{element.label}</li>
                          </ul>
                        ))}
                    </Box>
                  </Box>
                </Box>
                <Box className="valueForm">
                  <Box className="header">
                    <h1>{capitalizeFirstLetter(value_set_id)}</h1>
                  </Box>
                  <PrioritySlider
                    value={sliderValues[value_set_id] ?? 0}
                    onChange={(_, value) => {
                      setSliderValues({ ...sliderValues, [value_set_id]: value as number })
                    }}
                  ></PrioritySlider>
                </Box>
              </Box>
              <Box className="buttons">
                <Button
                  variant="text"
                  className="sliderButton"
                  css={{ color: 'inherit' }}
                  onClick={() =>
                    setShowElements({
                      ...showElements,
                      [value_set_id]: !showElements[value_set_id],
                    })
                  }
                >
                  View controls
                </Button>
                <Button
                  variant="text"
                  className="elementButton"
                  css={{ color: 'inherit' }}
                  onClick={() =>
                    setShowElements({
                      ...showElements,
                      [value_set_id]: !showElements[value_set_id],
                    })
                  }
                >
                  View elements
                </Button>
              </Box>
            </Box>
          ))}
        </Carousel>
      </Box>
    </Box>
  )
}
